import React from "react";
import styled from "styled-components";
import { SectionH2, GradientWord, Eyebrow } from "../components/Headlines";
import { FaBolt, FaLock, FaSearch, FaChartLine } from "react-icons/fa";

const StyledWhyMaintain = styled.section`
  background-color: ${(props) => props.theme.darkBg1};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 100px 30px;

  @media screen and (min-width: 800px) {
    padding: 120px 60px;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 720px;
  margin-bottom: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

const Subhead = styled.p`
  font-family: ${(props) => props.theme.lightFont};
  font-size: 18px;
  line-height: 1.6;
  color: ${(props) => props.theme.textMuted};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;
  max-width: 1000px;

  @media screen and (min-width: 800px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-start;
  padding: 28px;
  background: ${(props) => props.theme.panel};
  border: 1px solid ${(props) => props.theme.hairline};
  border-radius: 16px;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${(props) => props.theme.accentBorder};
  }
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: ${(props) => props.theme.accentSoft};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
    color: ${(props) => props.theme.accent};
  }
`;

const CardTitle = styled.h3`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 19px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
  margin: 0 0 8px 0;
`;

const CardText = styled.p`
  font-family: ${(props) => props.theme.lightFont};
  font-size: 15px;
  line-height: 1.55;
  color: ${(props) => props.theme.textMuted};
  margin: 0;
`;

const hooks = [
  {
    icon: FaBolt,
    title: "Fast",
    text: "Sub-second loads and clean Core Web Vitals. Google rewards it, visitors don't bounce, and it quietly lifts everything below.",
  },
  {
    icon: FaLock,
    title: "Secure",
    text: "Patched, hardened, and clean - a site as buttoned-up as the product you sell.",
  },
  {
    icon: FaSearch,
    title: "Found",
    text: "SEO keeps you ranking in Google. GEO keeps you showing up when buyers ask ChatGPT, Perplexity, and AI Overviews who to trust.",
  },
  {
    icon: FaChartLine,
    title: "Converting",
    text: "Built to turn visitors into pipeline - clear paths to demo and contact, tested and tuned, not guessed.",
  },
];

export default function WhyMaintain() {
  return (
    <StyledWhyMaintain id="why-maintain">
      <Header>
        <Eyebrow>What "maintained" actually means</Eyebrow>
        <SectionH2>
          Four jobs your website should <GradientWord>never stop</GradientWord>{" "}
          doing
        </SectionH2>
        <Subhead>
          The site you launch isn't the site you have a year later - unless
          someone's looking after it.
        </Subhead>
      </Header>
      <Grid>
        {hooks.map((h) => {
          const Icon = h.icon;
          return (
            <Card key={h.title}>
              <IconWrap>
                <Icon />
              </IconWrap>
              <div>
                <CardTitle>{h.title}</CardTitle>
                <CardText>{h.text}</CardText>
              </div>
            </Card>
          );
        })}
      </Grid>
    </StyledWhyMaintain>
  );
}
